import { GridColDef } from '@mui/x-data-grid-pro';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { UserKeys, getGroups } from '../../../../api/users-api';
import { useState, memo, useMemo } from 'react';
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { AddUserGroup, Group, Page } from '../../../../../types';
import StepGroupsToolbar from './step-groups-toolbar';
import { UserFormValues } from '../user-schema';
import SharedDataGridServer from '../../../../shared/shared-data-grid-server';

import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

// Memoized role selector component to prevent unnecessary re-renders
const RoleSelector = memo(
  ({
    groupId,
    groupIndex,
    control,
  }: {
    groupId: string;
    groupIndex: number;
    control: Control<UserFormValues>;
  }) => {
    return (
      <Controller
        key={groupId}
        name={`groups.${groupIndex}.member.role`}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            slotProps={{
              input: {
                id: `group-role-${groupId}`,
              },
              inputLabel: {
                htmlFor: `group-role-${groupId}`,
              },
            }}
            label="Role"
            select
            size="small"
            margin="none"
            defaultValue="MEMBER"
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="MEMBER">Member</MenuItem>
            <MenuItem value="MANAGER">Manager</MenuItem>
            <MenuItem value="OWNER">Owner</MenuItem>
          </TextField>
        )}
      />
    );
  }
);

interface StepGroupsProps {
  setValue: UseFormSetValue<UserFormValues>;
  watch: UseFormWatch<UserFormValues>;
  getValues: UseFormGetValues<UserFormValues>;
  control: Control<UserFormValues>;
}

export default function StepGroups({
  setValue,
  watch,
  control,
  getValues,
}: StepGroupsProps) {
  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
    cursor: string | null;
    search: string;
  }>({
    pageSize: 100,
    page: 0,
    cursor: null,
    search: '',
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      UserKeys.Groups,
      paginationModel.page,
      paginationModel.pageSize,
      paginationModel.search,
    ],
    queryFn: () => {
      const object: Page<Group[]> = {
        results: [],
        firstPull: false,
        searchTime: 0,
        pageSize: paginationModel.pageSize,
        cursor: paginationModel.cursor,
        nameSpace: '',
      };
      if (paginationModel.page !== 0 && data && data.queries) {
        object.queries = {
          [Object.keys(data.queries)[0]]: paginationModel.cursor || '',
        };
      }
      return getGroups(object, paginationModel.search);
    },
    placeholderData: keepPreviousData,
    meta: {
      errorMessage:
        'There was an issue loading groups. Please refresh and try again.',
    },
  });

  const groups = watch('groups');
  const currentGroups = useMemo(() => groups || [], [groups]);
  const [selectedRows, setSelectedRows] = useState<string[]>(
    currentGroups.map((g) => g.groupId) ?? []
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'groupEmail',
        headerName: 'Groups',
        sortable: false,
        filterable: false,
        display: 'flex',
        flex: 1,
        renderCell: (params) => {
          const groupIndex = currentGroups.findIndex(
            (g) => g.groupId === params.value
          );
          const isSelected = groupIndex !== -1;

          return (
            <Stack
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              sx={{ mr: 4 }}
            >
              <Typography variant="body2">{params.value}</Typography>

              {isSelected && (
                <RoleSelector
                  groupId={params.value}
                  groupIndex={groupIndex}
                  control={control}
                />
              )}
            </Stack>
          );
        },
      },
    ],
    [control, currentGroups]
  );

  const rows = useMemo(
    () =>
      data?.results.map((group) => ({
        ...group,
        id: group.email,
        groupId: group.email,
        groupEmail: group.email,
      })) || [],
    [data?.results]
  );
  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}
      gap={1}
      alignItems={'left'}
    >
      <Stack alignItems="left" sx={{ minHeight: 0 }}>
        <Typography variant="body2">
          Select any groups you'd like this user to be added to.
        </Typography>
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0, p: 0 }}>
        <SharedDataGridServer
          onPaginationModelChange={(newModel) => {
            if (isFetching || isLoading) return;

            if (newModel.pageSize !== paginationModel.pageSize) {
              setPaginationModel({
                page: 0,
                pageSize: newModel.pageSize,
                cursor: data?.cursor || '',
                search: paginationModel.search,
              });
            } else {
              setPaginationModel({
                ...newModel,
                cursor: data?.cursor || '',
                search: paginationModel.search,
              });
            }
          }}
          toolbarQuickFilterWidth={392}
          onFilterModelChange={(model) => {
            if (isFetching || isLoading) return;

            setPaginationModel({
              ...paginationModel,
              search: model.quickFilterValues?.[0] || '',
            });
          }}
          pageSizeOptions={[100]}
          data={data}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          onRowSelectionModelChange={(newSelection) => {
            const selectedGroupIds = Array.from(newSelection.ids) as string[];
            const newGroups: AddUserGroup[] = selectedGroupIds
              .map((groupId) => {
                const existingGroup = currentGroups.find(
                  (group) => group?.groupId === groupId
                );

                if (existingGroup) return existingGroup;

                const row = rows.find((row) => row.id === groupId);

                if (!row) return null;

                return {
                  groupId,
                  member: {
                    email: `${getValues('userName')}@${getValues('domain')}`,
                    role: 'MEMBER',
                    canActOn: false,
                  },
                };
              })
              .filter(Boolean) as AddUserGroup[];

            setValue('groups', newGroups);
          }}
          rows={rows || []}
          slots={{
            toolbar: StepGroupsToolbar,
          }}
          checkboxSelection
          disableColumnMenu
          keepNonExistentRowsSelected
          columns={columns}
          loading={isLoading || isFetching}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
            '& .MuiDataGrid-toolbar': {
              padding: '4px',
            },
            minWidth: 700,
          }}
        />
      </Box>
    </Box>
  );
}
